import SimpleSchema from 'simpl-schema';

import store from '../../config/configureStore';
import {
  crimeAgainstPersonCodes,
  crimeAgainstPropertyCodes,
  crimeAgainstSocietyCodes,
} from '../helpers/Codes/UCR';
import { getCategoryOfCode } from 'utils/functions';

const getOffenseCode = (codeOffenseCodes, code) =>
  codeOffenseCodes.find((codeOffenseCode) => codeOffenseCode === code);

export const incVictimSchema = () => {
  SimpleSchema.setDefaultMessages({
    messages: {
      en: store?.store.getState().incident.ibrsValidationRules?.incVictim || {},
    },
  });

  return new SimpleSchema({
    aggravatedAssaultHomicide: {
      type: String,
      optional: true,
      custom() {
        // NIBRS-2019.2-196: Victim Aggravated Assault Homicide Factor
        if (
          ['09A', '09B', '09C', '13A'].includes(this.obj.offense.values.statuteDetails?.FBICode)
        ) {
          if (
            !this.value ||
            (Array.isArray(this.value) && this.value.length === 0) ||
            this.value === '[]'
          )
            return 'NIBRS-2019.2-196';
        }

        if (!this.value) return;
        const parsedValues = JSON.parse(this.value);
        if (!parsedValues) return;

        const codeAggravatedAssault = store.store.getState().dictionary.codeAggAssaultType;
        const assaultCodes =
          parsedValues?.map((value) => getCategoryOfCode(codeAggravatedAssault, value)) || [];
        // NIBRS-2019.2-133: Victim Aggravated Assault Homicide Factor
        if (
          !['09A', '09B', '09C', '13A'].includes(this.obj.offense.values.statuteDetails?.FBICode)
        ) {
          if (parsedValues?.length) {
            return 'NIBRS-2019.2-133';
          }
        }

        // NIBRS-2019.2-134: Victim Aggravated Assault Homicide Factor
        if (assaultCodes?.includes('10')) {
          if (parsedValues?.length > 1) {
            return 'NIBRS-2019.2-134';
          }
        }

        // NIBRS-2019.2-136: Victim Aggravated Assault Homicide Factor
        if (['09A', '13A'].includes(this.obj.offense.values.statuteDetails?.FBICode)) {
          if (parsedValues?.length > 2) {
            return 'NIBRS-2019.2-136';
          }
        }

        // NIBRS-2019.2-137: Victim Aggravated Assault Homicide Factor
        if (this.obj.offense.values.statuteDetails?.FBICode === '09B') {
          if (parsedValues?.length > 1) {
            return 'NIBRS-2019.2-137';
          }
        }

        // NIBRS-2019.2-201: Victim Aggravated Assault Homicide Factor
        if (this.obj.offense.values.statuteDetails?.FBICode === '13A') {
          for (let value of assaultCodes) {
            if (
              ![
                '1',
                '2',
                '3',
                '4',
                '5',
                '6',
                '8',
                '9',
                '01',
                '02',
                '03',
                '04',
                '05',
                '06',
                '08',
                '09',
                '10',
              ].includes(value)
            ) {
              return 'NIBRS-2019.2-201';
            }
          }
        }

        // NIBRS-2019.2-138: Victim Aggravated Assault Homicide Factor
        if (this.obj.offense.values.statuteDetails?.FBICode === '09C') {
          if (parsedValues?.length > 1) {
            return 'NIBRS-2019.2-138';
          }
          if (parsedValues.length && parsedValues[0] !== '20' && parsedValues[0] !== '21') {
            return 'NIBRS-2019.2-197';
          }
        }

        // NIBRS-2019.2-132: Victim Aggravated Assault Homicide Factor
        let errorFlag = false;
        parsedValues.forEach((value) => {
          if (this.obj.incVictims?.length > 1) {
            for (let victim of this.obj.incVictims) {
              if (victim.incVictimDetails?.ptsVictimId === this.obj.incVictim?.ptsVictimId) {
                continue;
              }
              if (!victim.incVictimDetails?.values?.aggravatedAssaultHomicide) {
                break;
              }
              let parsed = JSON.parse(victim.incVictimDetails?.values?.aggravatedAssaultHomicide);
              if (!parsed) {
                break;
              }
              if (parsed.includes(value)) {
                errorFlag = true;
              }
            }
          }
        });
        if (errorFlag) {
          return 'NIBRS-2019.2-132';
        }
      },
    },
    injuryType: {
      type: String,
      label: 'Person Injury',
      optional: true,
      custom() {
        const injuryTypeStringArray = this.value ? this.value : '[]';
        const injuryType = JSON.parse(injuryTypeStringArray);
        const victimCategory = this.obj.incVictim.values.category;
        const FBICode = this.obj.offense.values.statuteDetails?.FBICode;
        const codeInjuryCategory = store.store.getState().dictionary.codeInjuryCategory;
        const codeVictimCategory = store.store.getState().dictionary.codeVictimCategory;
        const ibrsVersion = store.store.getState().user?.ibrsVersion;

        const injuryTypeCategory =
          injuryType?.map((value) => getCategoryOfCode(codeInjuryCategory, value)) || [];

        const codeVictimCategoryType = getCategoryOfCode(codeVictimCategory, victimCategory);

        //NIBRS NIBRS-2019.2-901: Person Injury
        if (!injuryType || !injuryType.length) {
          const FBICodes = [
            '09A',
            '09B',
            '09C',
            '100',
            '11A',
            '11B',
            '11C',
            '11D',
            '120',
            '13A',
            '13B',
            '210',
            '64A',
            '64B',
          ];
          if (
            FBICodes.some((item) => FBICode === item) &&
            ['i', 'l'].includes(codeVictimCategoryType)
          ) {
            return 'NIBRS-2019.2-901';
          }
        }

        // NIBRS-2019.2-143
        if (injuryType.length && victimCategory && !['i', 'l'].includes(codeVictimCategoryType)) {
          return 'NIBRS-2019.2-143';
        }

        // NIBRS-2019.2-146
        if (FBICode === '13B') {
          if (!injuryTypeCategory.includes('m') && !injuryTypeCategory.includes('n'))
            return 'NIBRS-2019.2-146';
        }

        // NIBRS-2019.2-141
        if (
          injuryType.length &&
          ![
            '09A',
            '09B',
            '09C',
            '100',
            '11A',
            '11B',
            '11C',
            '11D',
            '120',
            '13A',
            '13B',
            '210',
            '64A',
            '64B',
          ].find((element) => element === FBICode)
        ) {
          return 'NIBRS-2019.2-141';
        }

        //NIBRS-2019.2-144: Person Injury
        if (injuryType.length && injuryTypeCategory.includes('n') && injuryType.length > 1) {
          return 'NIBRS-2019.2-144';
        }

        //NIBRS-2019.2-145: Person Injury
        const invalidInjuryTypes = ['b', 'l', 'o', 't'];
        const shouldRaiseError =
          injuryType.length &&
          injuryTypeCategory.includes('m') &&
          invalidInjuryTypes.some((item) => injuryTypeCategory.includes(item));

        if (shouldRaiseError) return 'NIBRS-2019.2-145';

        if (ibrsVersion === 'TEXAS') {
          //TEXAS-2023.1-04
          if (FBICode === '13A' && this.obj.offense.values?.forceCategory) {
            const weaponForceValues = JSON.parse(this.obj.offense.values?.forceCategory);
            if (!weaponForceValues) return;

            const codeForceCategory = store.store.getState().dictionary.codeForceCategory;
            const weaponForceValuesCategory =
              weaponForceValues?.map((value) => getCategoryOfCode(codeForceCategory, value)) || [];

            if (
              weaponForceValuesCategory.includes('40') &&
              (injuryTypeCategory.includes('m') || injuryTypeCategory.includes('n'))
            ) {
              return 'TEXAS-2023.1-04';
            }
          }
        }
      },
    },
    category: {
      type: String,
      label: 'Victim Category',
      optional: true,
      custom() {
        if (!this.value) {
          return 'NIBRS-2019.2-115';
        }
        const codeVictimCategory = store.store.getState().dictionary.codeVictimCategory;
        const codeVictimCategoryType = getCategoryOfCode(
          codeVictimCategory,
          this.obj.incVictim.values.category
        );

        // NIBRS-2019.2-116
        if (
          getOffenseCode(crimeAgainstPersonCodes, this.obj.offense.values.statuteDetails?.FBICode)
        ) {
          if (!['i', 'l'].includes(codeVictimCategoryType)) {
            return 'NIBRS-2019.2-116';
          }
        }

        // NIBRS-2019.2-117
        if (
          getOffenseCode(crimeAgainstSocietyCodes, this.obj.offense.values.statuteDetails?.FBICode)
        ) {
          if (codeVictimCategoryType !== 's') {
            return 'NIBRS-2019.2-117';
          }
        }

        // NIBRS-2019.2-118
        if (
          getOffenseCode(crimeAgainstPropertyCodes, this.obj.offense.values.statuteDetails?.FBICode)
        ) {
          if (codeVictimCategoryType === 's') {
            return 'NIBRS-2019.2-118';
          }
        }

        // NIBRS-2019.2-119: Victim Type
        if (codeVictimCategoryType === 'l') {
          if (
            !['09A', '13A', '13B', '13C'].includes(this.obj.offense.values.statuteDetails?.FBICode)
          ) {
            return 'NIBRS-2019.2-119';
          }
        }
      },
    },
    victimType: {
      type: String,
      optional: true,
      custom() {
        const codeVictimCategory = store.store.getState().dictionary.codeVictimCategory;

        //NIBRS-2019.2-183: Victim Type
        //TODO: Improve the code logic
        if (this.obj.incVictims.length > 1) {
          let isSociety;

          for (let incVictim of this.obj.incVictims) {
            if (
              getCategoryOfCode(codeVictimCategory, incVictim.incVictimDetails.values.category) ===
              's'
            ) {
              isSociety = true;
              break;
            }
          }

          if (isSociety) {
            return 'NIBRS-2019.2-183';
          }
        }
      },
    },
    justifiableHomicide: {
      type: String,
      optional: true,
      custom() {
        // NIBRS-2019.2-140: Victim Justifiable Homicide Factor
        if (this.value) {
          if (this.obj.offense.values.statuteDetails?.FBICode !== '09C') {
            return 'NIBRS-2019.2-140';
          }
        }
      },
    },
    additionalJustifiableHomicide: {
      type: String,
      optional: true,
      custom() {
        // NIBRS-2019.2-142: Victim Additional Justifiable Homicide Factor
        if (
          this.obj.offense.values.statuteDetails?.JustifiableHomicide === true &&
          this.obj.incVictim.values?.aggravatedAssaultHomicide
        ) {
          const parsedValues = JSON.parse(this.obj.incVictim.values?.aggravatedAssaultHomicide);
          if (!parsedValues) return;

          const codeAggravatedAssault = store.store.getState().dictionary.codeAggAssaultType;
          const assaultCodes =
            parsedValues?.map((value) => getCategoryOfCode(codeAggravatedAssault, value)) || [];

          if (assaultCodes?.includes('20') || assaultCodes?.includes('21')) {
            if (!this.value) {
              return 'NIBRS-2019.2-142';
            }
          }
        }

        // NIBRS-2019.2-210: Victim Additional Justifiable Homicide Factor
        if (this.obj.offense.values.statuteDetails?.JustifiableHomicide === false) {
          if (this.value) {
            return 'NIBRS-2019.2-210';
          }
        }
      },
    },
    officerActivity: {
      type: String,
      optional: true,
      custom() {
        // NIBRS-2019.2-121: OfficerActivity
        const codeVictimCategory = store.store.getState().dictionary.codeVictimCategory;

        if (
          this.obj.offense.values.statuteDetails?.OfficerActivity === true &&
          getCategoryOfCode(codeVictimCategory, this.obj.incVictim.values.category) === 'l'
        ) {
          if (!this.obj.incVictim.values.officerAssignment) {
            return 'NIBRS-2019.2-121(Enforcement Official Activity Category)';
          }
        }

        // NIBRS-2019.2-211: OfficerActivity
        if (
          this.obj.offense.values.statuteDetails?.OfficerActivity === false &&
          getCategoryOfCode(codeVictimCategory, this.obj.incVictim.values.category) !== 'l'
        ) {
          if (this.obj.incVictim.values.officerAssignment) {
            return 'NIBRS-2019.2-211';
          }
        }
      },
    },
    officerAssignment: {
      type: String,
      optional: true,
      custom() {
        // NIBRS-2019.2-121: OfficerActivity
        const codeVictimCategory = store.store.getState().dictionary.codeVictimCategory;
        if (
          this.obj.offense.values.statuteDetails?.OfficerActivity === true &&
          getCategoryOfCode(codeVictimCategory, this.obj.incVictim.values.category) === 'l'
        ) {
          if (!this.obj.incVictim.values.officerAssignment) {
            return 'NIBRS-2019.2-121(Enforcement Official Assignment Category)';
          }
        }

        // NIBRS-2019.2-212: OfficerActivity
        if (
          this.obj.offense.values.statuteDetails?.OfficerActivity === false &&
          getCategoryOfCode(codeVictimCategory, this.obj.incVictim.values.category) !== 'l'
        ) {
          if (this.obj.incVictim.values.officerAssignment) {
            return 'NIBRS-2019.2-212';
          }
        }
      },
    },
    officerORI: {
      type: String,
      optional: true,
      custom() {
        // NIBRS-2019.2-213: OfficerActivity
        const codeVictimCategory = store.store.getState().dictionary.codeVictimCategory;

        if (
          this.obj.offense.values.statuteDetails?.OfficerActivity === false &&
          getCategoryOfCode(codeVictimCategory, this.obj.incVictim.values.category) !== 'l'
        ) {
          if (this.obj.incVictim.values.officerORI) {
            return 'NIBRS-2019.2-213';
          }
        }
      },
    },
    otherFellonyInvolved: {
      type: String,
      optional: true,
      custom() {
        // NIBRS-2019.2-196: Victim Aggravated Assault Homicide Factor

        const codeAggravatedAssault = store.store.getState().dictionary.codeAggAssaultType;
        let isOtherFellonyInvolved = false;
        for (let incVictim of this.obj.incVictims) {
          const factorValue = incVictim.incVictimDetails.values.aggravatedAssaultHomicide;
          if (!factorValue) continue;
          const parsedValues = JSON.parse(factorValue);
          if (!parsedValues) continue;

          const assaultCodes =
            parsedValues?.map((value) => getCategoryOfCode(codeAggravatedAssault, value)) || [];

          if (assaultCodes?.includes('8') || assaultCodes?.includes('08')) {
            isOtherFellonyInvolved = true;
            break;
          }
        }

        // NIBRS-2019.2-204: When Aggravated Assault/Homicide Circumstances is 08 = Other Felony Involved,
        // incident must contain multiple offense entries of (09A, 09B, 13A) or multiple victim entries of victim category I = Individual.

        if (isOtherFellonyInvolved) {
          const numberOfIndividualVictims = this.obj.incVictims.filter(
            (victim) =>
              victim.incVictimDetails?.values?.category === 'I' ||
              victim.incVictimDetails?.values?.category === 'i'
          ).length;

          const numberOfOtherOffensesWithSameFBICode = this.obj.otherOffenses.filter(
            (offense) =>
              offense.offenseDetails?.values?.statuteDetails?.FBICode ===
              this.obj.offense.values.statuteDetails?.FBICode
          ).length;
          if (numberOfIndividualVictims < 2 && numberOfOtherOffensesWithSameFBICode === 0) {
            return 'NIBRS-2019.2-204';
          }
        }
      },
    },
    negligentManslaughter: {
      type: String,
      optional: true,
      custom() {
        // NIBRS-2019.2-208: Negligent Manslaughter
        if (
          !['09A', '09B', '09C', '13A'].includes(this.obj.offense.values.statuteDetails?.FBICode)
        ) {
          if (this.obj.incVictim?.values?.negligentManslaughter) return 'NIBRS-2019.2-208';
        }
      },
    },
    treatment: {
      type: String,
      optional: true,
      custom() {
        // NIBRS-2019.2-214: Victim Treatment
        const codeVictimCategory = store.store.getState().dictionary.codeVictimCategory;

        if (
          !['i', 'l', 'u'].includes(
            getCategoryOfCode(codeVictimCategory, this.obj.incVictim.values.category)
          )
        ) {
          if (this.obj.incVictim?.values?.treatment) {
            return 'NIBRS-2019.2-214';
          }
        }
      },
    },
    residentStatus: {
      type: String,
      optional: true,
      custom() {
        // NIBRS-2019.2-129 Resident
        const codeVictimCategory = store.store.getState().dictionary.codeVictimCategory;

        if (
          !['i', 'l'].includes(
            getCategoryOfCode(codeVictimCategory, this.obj.incVictim.values.category)
          )
        ) {
          if (this.obj.incVictim?.values?.residentStatus) {
            return 'NIBRS-2019.2-129 Resident';
          }
        }
      },
    },
  });
};
